import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, STATUS_LABEL, hr, downloadAsBlob, statusBadgeVariant } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Download, FileText, HomeIcon, CheckCircle2 } from "lucide-react";

export default function WFHRequestDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get(`/wfh/requests/${id}`).then(x => setR(x.data));
    api.get(`/audit/${id}`).then(x => setAudit(x.data));
  }, [id]);

  if (!r) return <div className="p-12 text-center">Učitavanje...</div>;

  const downloadPdf = () => {
    downloadAsBlob(`/wfh/requests/${id}/pdf`, `Odluka_WFH_${id.slice(0,6)}.pdf`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="wfh-detail">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <HomeIcon className="w-3 h-3" /> Rad na izdvojenom mjestu rada
          </p>
          <h1 className="serif text-3xl font-semibold">{r.employee_name}</h1>
          <p className="text-muted-foreground text-sm">{r.employee_katedra}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={statusBadgeVariant(r.status)} className="text-sm py-1 px-3">{STATUS_LABEL[r.status]}</Badge>
          {(r.status === "decided" || r.status === "sent") && (
            <Button onClick={downloadPdf} variant="outline" data-testid="wfh-detail-pdf-btn"><Download className="w-4 h-4 mr-2"/>PDF</Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Razdoblje i detalji</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div><span className="font-medium">Razdoblje:</span> {hr(r.start_date)} – {hr(r.end_date)}</div>
          {r.broj_mobitela && (
            <div><span className="font-medium">Broj mobitela:</span> {r.broj_mobitela}</div>
          )}
          {r.razlog && (
            <div className="pt-2"><span className="font-medium">Napomena zaposlenika:</span><br/>{r.razlog}</div>
          )}
          {r.dodatne_napomene && <div className="pt-2"><span className="font-medium">Dodatne napomene:</span><br/>{r.dodatne_napomene}</div>}
          {r.izjava_prihvacena && (
            <div className="pt-2 text-emerald-700 text-xs flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5"/>
              <span>Izjava (4 točke) prihvaćena pri predaji.</span>
            </div>
          )}
          {/* Legacy fields (samo za stare zahtjeve podnesene prije Iter39) */}
          {r.lokacija && (
            <div className="pt-2 text-xs text-muted-foreground"><span className="font-medium">Lokacija (stari zapis):</span> {r.lokacija}</div>
          )}
          {r.postotak_radnog_vremena && r.postotak_radnog_vremena !== 100 && (
            <div className="text-xs text-muted-foreground"><span className="font-medium">Postotak rad. vrem. (stari zapis):</span> {r.postotak_radnog_vremena}%</div>
          )}
        </CardContent>
      </Card>

      {r.manager_comment && (
        <Card><CardHeader><CardTitle className="text-base">Komentar voditelja</CardTitle></CardHeader>
          <CardContent><p className="text-sm">{r.manager_comment}</p></CardContent></Card>
      )}

      {(r.klasa || r.sharepoint_path) && (
        <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4"/>Službeni dokument</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            {r.klasa && <div>KLASA: <code className="bg-secondary px-1.5 py-0.5 rounded">{r.klasa}</code></div>}
            {r.urbroj && <div>URBROJ: <code className="bg-secondary px-1.5 py-0.5 rounded">{r.urbroj}</code></div>}
            {r.sharepoint_path && <div>SharePoint: <code className="bg-secondary px-1.5 py-0.5 rounded">{r.sharepoint_path}</code></div>}
          </CardContent></Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Povijest</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm"><span>Predano</span><span className="text-muted-foreground">{hr(r.created_at)}</span></div>
            {audit.map(a => (
              <div key={a.id} className="flex justify-between text-sm border-l-2 border-accent pl-3">
                <span><span className="font-medium">{a.action}</span> · {a.actor_email} {a.detail && `— ${a.detail}`}</span>
                <span className="text-muted-foreground">{hr(a.timestamp)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
